import { supabase } from './supabase';

/**
 * Wrappers finos sobre supabase.auth.* para o app de faturamento.
 * Padrao alinhado com admin-etus / outros apps do ecossistema.
 */

// =============================================================================
// Dominios de e-mail permitidos no login
// =============================================================================
// Primeira camada de validacao apos o callback OAuth: rejeita qualquer
// usuario cujo e-mail nao termine em um destes sufixos. Roda ANTES do
// app_access/scopes, entao quem usa @gmail.com etc nem chega a bater no
// banco. Cada item precisa comecar com '@' — o filter abaixo descarta
// strings que nao tem '@' inicial (cobre o placeholder de comentario).
//
// Pra adicionar um dominio novo, descomente/edite a linha placeholder
// abaixo OU adicione uma nova entrada no formato '@dominio.tld'.
export const ALLOWED_DOMAINS = [
  '@etus.com.br',
  '@brius.com.br',
  '@bhaz.com.br',
  '@evofundation.com.br',
  // ei johnny insira aqui o novo dominio (ex: @brius.com.br)
];

/**
 * Retorna true se o e-mail termina em algum dos sufixos de ALLOWED_DOMAINS.
 * Tudo case-insensitive. Filtra entradas invalidas defensivamente (so
 * considera as que comecam com '@').
 */
export function isDomainAllowed(email) {
  const lower = String(email || '').toLowerCase().trim();
  if (!lower) return false;
  return ALLOWED_DOMAINS
    .filter(d => typeof d === 'string' && d.startsWith('@'))
    .some(d => lower.endsWith(d.toLowerCase()));
}

export async function signIn({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: String(email || '').trim().toLowerCase(),
    password: String(password || ''),
  });
  if (error) throw error;
  return data;
}

/**
 * Login via Google OAuth (Supabase). hd=etus.com.br restringe ao
 * Google Workspace da empresa — qualquer @gmail.com ou outro dominio
 * e barrado pelo Google antes mesmo de chegar no callback.
 *
 * Apos OAuth o Google volta pra mesma origem/path, o Supabase processa
 * o hash sincronamente na inicializacao do client, o listener de
 * useSession captura a sessao e o App.jsx troca de tela.
 *
 * Mesmo padrao usado em relatorios/fluxo de caixa.
 */
export async function signInWithGoogle() {
  if (typeof window === 'undefined') return;
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}${window.location.pathname}`,
      queryParams: {
        hd: 'etus.com.br',
      },
    },
  });
  if (error) throw error;
}

/**
 * Limpa o # que sobra na URL APOS o Supabase consumir o token OAuth.
 *
 * CRITICO: nunca remove um hash que ainda contem access_token/refresh_token —
 * o Supabase le esse hash de forma assincrona dentro de _handleOnInit().
 * Se a gente limpar antes, o Supabase nao acha o token, a sessao nao
 * e criada, e o usuario volta pra tela de login (bug que afetou esse
 * app). So limpamos hashes "consumidos" ou vazios.
 *
 * Por isso esta funcao deve ser chamada DEPOIS de `supabase.auth.getSession()`
 * (que aguarda o initialize do client) — nao no mount sincrono do App.
 */
export function cleanOAuthHash() {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash || '';
  // Skip se Supabase ainda pode estar lendo (token ainda no hash).
  if (hash.includes('access_token') || hash.includes('refresh_token') || hash.includes('error')) {
    return;
  }
  // Hash vazio ou ja consumido — limpa o '#' cosmetico que sobra.
  if (window.location.href.includes('#')) {
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * Verifica se o usuario logado tem app_access pra 'faturamento'.
 * Usa a function helper public.has_faturamento_access() criada via SQL.
 * Retorna boolean.
 */
export async function hasFaturamentoAccess() {
  const { data, error } = await supabase
    .schema('public')
    .rpc('has_faturamento_access');
  if (error) {
    console.error('Erro ao checar acesso:', error);
    return false;
  }
  return Boolean(data);
}

/**
 * Retorna { hasAccess, isAdmin, scopes } do usuario pra 'faturamento'.
 * Usado pra controle granular: admins veem principal + tudo; nao-admins
 * so podem acessar URLs cujos slugs estao em scopes.
 *
 * Fallback: se a RPC get_my_faturamento_access nao existe ainda (user nao
 * rodou o SQL), cai pra has_faturamento_access (boolean). Nesse modo,
 * tudo eh tratado como admin pra nao bloquear o user.
 */
export async function getMyFaturamentoAccess() {
  const r = await supabase.schema('public').rpc('get_my_faturamento_access');
  if (!r.error) {
    const row = Array.isArray(r.data) ? r.data[0] : r.data;
    if (row) {
      return {
        hasAccess: true,
        isAdmin: !!row.is_admin,
        scopes: Array.isArray(row.scopes) ? row.scopes : [],
      };
    }
    // RPC funcionou mas user nao tem app_access pra faturamento
    return { hasAccess: false, isAdmin: false, scopes: [] };
  }

  // RPC falhou — provavelmente o SQL ainda nao foi rodado.
  // Fallback pra has_faturamento_access (boolean) e assume admin.
  console.warn('[auth] get_my_faturamento_access falhou, fallback pra has_faturamento_access:', r.error);
  const has = await hasFaturamentoAccess();
  return { hasAccess: has, isAdmin: has, scopes: [] };
}
